<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <div class="d-flex align-items-center">
        <i class="fas fa-list"></i> {{ $t('page.title.question_list') }}
      </div>
      <template v-slot:right>
        <b-button :disabled="isExportInProgress" @click="exportWithFilters(typeFilter)" size="sm" class="ml-1" variant="warning">
          {{ $t('filters.btn.export') }}
          <b-spinner v-if="isExportInProgress" small type="grow" label="aaaaa">Loading...</b-spinner>

        </b-button>
        <b-button @click="openModal('create')" size="sm" class="ml-1" variant="success">{{
            $t('btn.add_new')
          }}
        </b-button>
      </template>
    </page-header>
    <page-card>
      <template v-slot:header>
        <div class="d-flex align-items-center">
          {{ $t('page.filters') }}
          <div class="d-flex align-items-center ml-4">
            <div class="font-weight-normal text-nowrap">{{ $t('page.list.question_language') }}</div>
            <b-form-select class="ml-2" v-model="questionsLanguage">
              <option
                  v-for="(option, idx) in locale"
                  :key="idx"
                  :value="option.label"
                  :title="option.name || null"
              >
                {{ option.label }}
              </option>
            </b-form-select>
          </div>
        </div>
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="phrase"
                  :label="$t('filters.label.question_list_phrase')"
                  :placeholder="$t('filters.placeholder.question_list_phrase')">
              </filter-input>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="selectOptions.type"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  @type="getTypeFilterValue($event)"
                  filter-name="type"
                  :label="$t('filters.label.type')"
                  :placeholder="$t('filters.placeholder.type')">
              </filter-select>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="selectOptions.status"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="status"
                  :label="$t('filters.label.status')"
                  :placeholder="$t('filters.placeholder.status')">
              </filter-select>
            </div>
          </div> <!-- #row-->

          <div class="row">
            <div class="col-4">
              <filter-select-with-operator
                  :filter-query="filterQuery"
                  :operator-options="selectOptions.containOperator"
                  :operator-multiple="false"
                  :default-operator="{value: 'contain', text: $t('operator.contains')}"
                  :value-options="locale"
                  :value-multiple="false"
                  options-label="label"
                  options-track-by="label"
                  filter-name="language"
                  :label="$t('filters.label.language')">
              </filter-select-with-operator>
            </div>

            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="selectOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="{value: 'eq', text: $t('operator.equales')}"
                  :restrict-input="true"
                  restriction-type="numbers"
                  filter-name="difficulty"
                  :label="$t('filters.label.difficulty_level')">
              </filter-input-with-operator>
            </div>

            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="createdAt"
                  open-direction="left"
                  :label="$t('filters.label.created_at')">
              </filter-date-range-picker>
            </div>
          </div> <!-- #row-->

          <div class="row">
            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="selectOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="{value: 'eq', text: $t('operator.equales')}"
                  :restrict-input="true"
                  restriction-type="numbers"
                  filter-name="numId"
                  :label="$t('filters.label.question_id')">
              </filter-input-with-operator>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="selectOptions.category"
                  :multiple="true"
                  options-label="translated"
                  options-track-by="id"
                  :operator="'contain'"
                  filter-name="category"
                  :label="$t('filters.label.category')"
                  :placeholder="$t('filters.placeholder.category')">
              </filter-select>
            </div>

            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="updatedAt"
                  open-direction="left"
                  :label="$t('filters.label.updated_at')">
              </filter-date-range-picker>
            </div>
          </div> <!-- #row-->

          <div class="row">
            <div class="col-12">
              <div class="float-left">
                <b-checkbox v-model="showDeleted">
                  <span class="font-weight-normal">{{ $t('filters.label.include_deleted_questions') }}</span>
                </b-checkbox>
              </div>
              <div class="float-right">
              </div>
            </div>
          </div> <!-- #row-->
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{ $t('filters.btn.clear') }}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{ $t('filters.btn.apply') }}</b-button>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        <div class="d-flex align-items-center">
          {{ $t('page.list') }}
        </div>
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="pagination-wrapper">
            <bulk-actions-select
                @bulk="setBulk"
                :value="bulkAction"
                :options="selectOptions.bulkActions"
                :is-busy="isBusy"
                :selected-rows="selectedRows"
                @bulkDeleteAction="callBulkAction(selectedRows, 'delete')"
                @bulkPublishAction="callBulkAction(selectedRows, 'publish')"
                @bulkDraftAction="callBulkAction(selectedRows, 'draft')"
                @exportDraftAction="callExportBulkAction(selectedRows)"
            >
            </bulk-actions-select>
            <grid-pagination :total-count="totalCount"
                             :per-page="perPage"
                             :current-page="currentPage"
                             @reset-checkbox="resetBulkActions"
                             @load-items="paginationReload"></grid-pagination>
          </div> <!-- #pagination-wrapper-->

          <div class="table-responsive">
            <b-table striped show-empty
                     :empty-text="$t('table.no_results')"
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-by.sync="orderColumn"
                     :sort-desc.sync="orderDirection"
                     :no-local-sorting="true"
                     :items="items"
                     ref="table"
                     :fields="fields">
              <template #head(id)="data">
                <b-checkbox
                    v-model="selectAllCheckbox"
                    @change="toggleAllCheckboxes($event, data)">
                </b-checkbox>
              </template>

              <template #cell(id)="data">
                <b-checkbox v-model="selectedRows"
                            :key="data.value"
                            :value="data.value">
                </b-checkbox>
              </template>

              <template #cell(question)="data">
                <div>
                  <div v-for="item in data.item.question" :key="item.id">
                    <span v-if="item.locale === questionsLanguage">{{ item.content }}</span>
                  </div>
                  <div v-if="!data.item.question.filter(question => question.locale === questionsLanguage).length">
                    {{ $t('table.question.missing_translation') }}
                  </div>
                </div>
              </template>

              <template #cell(answers)="data">
                <div class="table--answers">
                  <div v-for="(ans, index) in data.value" :key="index">
                    <b-badge :variant="data.item.correctAnswer === (index+1) ? 'success' : ''">
                      {{ ans }}
                    </b-badge>
                  </div>
                </div>
              </template>

              <template #cell(answers)="data">
                <div>
                  <div v-for="(item, index) in data.item.answers" :key="index">
                    <span v-if="item.locale === questionsLanguage">
                      <div v-for="(ans, index2) in item.answers" :key="index2">
                        <b-badge :variant="data.item.correctAnswer === (index2+1) ? 'success' : ''">
                          {{ ans }}
                        </b-badge>
                     </div>
                    </span>
                  </div>
                  <div class="question-row"
                       v-if="!data.item.answers.filter(question => question.locale === questionsLanguage).length">
                    {{ $t('table.question.missing_translation') }}
                  </div>
                </div>
              </template>

              <template #cell(languages)="data">
                <div class="table-flag--wrapper">
                  <span v-for="(lang, index) in data.value" :key="index">

                      <img :src="require(`@/assets/flags/${lang.toLowerCase()}.svg`)"
                           class="table-flags mr-1"> {{ lang.toUpperCase() }}

                  </span>
                </div>
              </template>

              <template #cell(createdAt)="data">
                <div class="table-flag--wrapper">
                  <div>{{ data.item.createdAt | date }}</div>
                </div>
              </template>

              <template #cell(type)="data">
                <div>
                  {{ $t('question_type_' + data.value) }}
                </div>
              </template>

              <template #cell(updatedAt)="data">
                <div class="table-flag--wrapper">
                  <div>{{ data.item.updatedAt | date }}</div>
                </div>
              </template>

              <template #cell(categories)="data">
                <div class="table-categories--wrapper" v-for="(cat, index) in data.value" :key="index">
                  <b-badge variant="light">{{ $t('category.' + cat) }}</b-badge>
                </div>
              </template>

              <template #cell(status)="data">
                <b-badge pill variant="dark" v-if="data.value == 30">{{ $t('question_status_' + data.value) }}</b-badge>
                <b-badge pill variant="success" v-if="data.value == 20">{{ $t('question_status_' + data.value) }}
                </b-badge>
                <b-badge pill variant="warning" v-if="data.value == 10">{{ $t('question_status_' + data.value) }}
                </b-badge>
              </template>

              <template #cell(difficulty)="data">
                <div v-if="data.item.difficulty == 1">{{ $t('question.easy') }}</div>
                <div v-if="data.item.difficulty == 2">{{ $t('question.medium') }}</div>
                <div v-if="data.item.difficulty == 3">{{ $t('question.difficult') }}</div>
                <div v-if="data.item.difficulty == 4">{{ $t('question.very_difficult') }}</div>
              </template>

              <template #cell(actions)="data">
                <div class="table-action--cell">
                  <b-button variant="success" size="sm" @click="openModal('edit', data.item, data.index)">
                    {{ $t('table.btn.edit') }}
                  </b-button>
                  <b-button variant="primary" size="sm" @click="openModal('show', data.item)">
                    {{ $t('table.btn.show') }}
                  </b-button>

                  <b-button variant="danger" size="sm"
                            v-if="data.item.status != 30"
                            @click="deleteItem(data.item)">
                    {{ $t('table.btn.delete') }}
                  </b-button>
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{ $t('state.loading') }}</strong>
                </div>
              </template>

            </b-table>
          </div> <!-- #table-responsive-->

          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @reset-checkbox="resetBulkActions"
                           @load-items="paginationReload"></grid-pagination>

        </div>
      </template>
      <template v-slot:footer>
      </template>
    </page-card>
  </div>

</template>

<script>
import FilterInput from "../../components/grid/filters/FilterInput";
import FilterInputWithOperator from "../../components/grid/filters/FilterInputWithOperator";
import FilterSelect from "../../components/grid/filters/FilterSelect";
import FilterSelectWithOperator from "../../components/grid/filters/FilterSelectWithOperator";
import FilterDateRangePicker from "../../components/grid/filters/FilterDateRangePicker";
import GridMixin from '../../components/grid/GridMixin';
import BulkActionsMixin from '../../components/grid/BulkActionsMixin';
import BulkActionsSelect from "../../components/grid/bulk-actions/BulkActionsSelect";
import CreateModal from "./CreateModal";
import axios from "axios";
import GridPagination from "../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../components/page/PageHeader";
import PageCard from "../../components/page/PageCard";
import ShowQuestionModal from "./ShowQuestionModal";
import EditQuestionModal from "./EditQuestionModal";
import alertify from "alertifyjs";

export default {
  mixins: [GridMixin, BulkActionsMixin],
  components: {
    PageCard,
    PageHeader,
    BulkActionsSelect,
    FilterSelect,
    FilterSelectWithOperator,
    FilterInput,
    FilterInputWithOperator,
    FilterDateRangePicker,
    GridPagination,
  },
  data() {
    return {
      gridGetUrl: '/api/question/question',
      deleteActionUrl: '/api/question/bulk-delete',
      publishActionUrl: '/api/question/bulk-publish',
      draftActionUrl: '/api/question/bulk-draft',
      exportActionUrl: '/api/question/export',
      categoryGetUrl: '/api/question/question-category',
      localeGetUrl: '/api/backoffice/locale',
      showDeleted: false,
      orderColumn: 'createdAt',
      orderDirection: false,
      bulkAction: null,
      questionsLanguage: null,
      fields: [
        {key: 'id'},
        {key: 'numId', label: this.$t('table.header.id')},
        {key: 'question', label: this.$t('table.header.question'), class: 'question-row'},
        {key: 'answers', label: this.$t('table.header.answers'), class: 'answers-row'},
        {key: 'languages', label: this.$t('table.header.languages')},
        {key: 'type', label: this.$t('table.header.type'), sortable: true},
        {key: 'difficulty', label: this.$t('table.header.difficulty_level'), class: 'lvl-row', sortable: true},
        {key: 'categories', label: this.$t('table.header.category')},
        {key: 'status', label: this.$t('table.header.status'), sortable: true},
        {key: 'createdAt', label: this.$t('table.header.created_at'), sortable: true},
        {key: 'updatedAt', label: this.$t('table.header.updated_at'), sortable: true},
        {key: 'actions', label: this.$t('table.header.actions')}
      ],
      items: [],
      selectOptions: {
        questionLanguages: [
          {value: 'pl', text: 'Poland'},
          {value: 'en', text: 'English'}
        ],
        status: [
          {value: 20, text: this.$t('option.published')},
          {value: 10, text: this.$t('option.draft')},
          {value: 30, text: this.$t('option.deleted')}
        ],
        type: [
          {value: 'text', text: this.$t('option.text')},
          {value: 'image', text: this.$t('option.image')},
          {value: 'sound', text: this.$t('option.sound')},
          {value: 'video', text: this.$t('option.video')},
        ],
        containOperator: [
          {value: 'contain', text: this.$t('operator.contains')},
          {value: 'notContain', text: this.$t('operator.not_contains')},
        ],
        comparisonOperator: [
          {value: 'eq', text: this.$t('operator.equales')},
          {value: 'gte', text: this.$t('operator.greater_then')},
          {value: 'lte', text: this.$t('operator.lower_than')},
          {value: 'btw', text: this.$t('operator.between')},
        ],
        bulkActions: [
          {value: null, text: this.$t('table.bulk.actions')},
          {value: 'delete', text: this.$t('table.bulk.actions.delete')},
          {value: 'publish', text: this.$t('table.bulk.actions.publish')},
          {value: 'draft', text: this.$t('table.bulk.actions.draft')},
          {value: 'export', text: this.$t('table.bulk.actions.export')}
        ],
        difficulty: [
          {value: 1, text: this.$t('option.easy')},
          {value: 2, text: this.$t('option.medium')},
          {value: 3, text: this.$t('option.difficult')},
          {value: 4, text: this.$t('option.very_difficult')}
        ],
        lang: [],
        category: [],
      },
      typeFilter: null,
      isExportInProgress: false,
    };
  },
  mounted() {
    this.getData(this.categoryGetUrl + '?limit=1000', ['selectOptions', 'category']);
    this.questionsLanguage = this.userLocale;
  },
  methods: {
    resetBulkActions() {
      this.selectedRows = [];
      this.selectAllCheckbox = false;
    },
  exportWithFilters: function (typeFilter) {
    if(typeFilter === null){
      alertify.error(this.$t('question_export_missing_type'));
      return;
    }

    this.isExportInProgress = true;
    this.applyFilters();
    const filters = this.createFilterQuery();

    axios.get(`/api/question/export/${typeFilter}?${filters}`)
        .then(resp => {
          let url = window.URL.createObjectURL(new Blob([resp.data]));
          let downloadLink = document.createElement('a');
          downloadLink.setAttribute('href', url)
          downloadLink.setAttribute('download', `question-export-${new Date().getTime()}.csv`);
          downloadLink.click();
          this.isExportInProgress = false;
        })
        .catch(() => {
          alertify.error(this.$t('question_export_error'));
          this.isExportInProgress = false;
        });

  },
    callExportBulkAction(row) {
      this.isBusy = true;

      let httpMethod = 'post';
      let url = this.exportActionUrl;
      let valid = true;

      const config = {ids: row};

      const filteredItems = this.items.filter(item => {
        let question;

        row.forEach(id => {
          if (id === item.id) {
            question = item;
          }
        });

        return question;
      });

      const exampleQuestionType = filteredItems[0].type;

      let everyQuestionsTypeAreSame = filteredItems.every(data => data.type === exampleQuestionType);

      if (valid && !everyQuestionsTypeAreSame) {
        alertify.error(this.$t('question_export_error_different_types'));
        valid = false;
      }

      if(!valid){
        this.loadItems();
        return;
      }

      axios[httpMethod](url, config)
          .then((resp) => {
            this.loadItems();
            this.selectedRows = [];
            this.selectAllCheckbox = false;
            alertify.success('Action success.');

            let url = window.URL.createObjectURL(new Blob([resp.data]));
            let downloadLink = document.createElement('a');
            downloadLink.setAttribute('href', url)
            downloadLink.setAttribute('download', `export-${new Date().getTime()}.csv`);
            downloadLink.click(); // this will download the file
          })
          .catch(errors => {
            this.loadItems();
            console.error(errors);
            alertify.error('Action fail.');
          });
    },
    getTypeFilterValue(value) {
      this.typeFilter = value;
    },
    getData(url, localStorage) {
      axios.get(url)
          .then((resp) => {
            if (localStorage[1] === 'category') {
              this[localStorage[0]][localStorage[1]] = resp.data.items.map(c => {
                c.translated = this.$t('category.' + c.name);
                return c;
              });
            } else {
              this[localStorage[0]][localStorage[1]] = resp.data.items;
            }
          })
          .catch((err) => {
            console.log(err)
          })
    },
    setBulk(data) {
      this.bulkAction = data;
    },
    getLocale() {
      this.getData(this.localeGetUrl, ['selectOptions', 'lang']);
    },
    toggleDeletedAmongStatuses: function () {
      if (true === this.showDeleted) {
        this.selectOptions.status.push({value: 30, text: 'deleted'},)
      } else {
        this.selectOptions.status.splice(2, 1);
      }
    },
    importItems: function () {
      console.log('TODO: add backend support');
      console.log(this.user);
    },
    deleteItem: function (item) {
      let _that = this;
      this.$bvModal.msgBoxConfirm(this.$t('question.delete.title') + item.question, {
        title: this.$t('question.delete.are_you_sure'),
        okTitle: this.$t('btn.delete'),
        cancelTitle: this.$t('btn.cancel'),
        okVariant: 'danger',
      })
          .then(value => {
            if (value) {
              axios.delete('/api/question/delete/' + item.id)
                  .then(function () {
                    _that.loadItems();
                  })
                  .catch(error => {
                    console.log(error);
                  });
            }
          });
    },
    createFilterQuery() {
      let filter = '';
      Object.keys(this.filterQuery).forEach(key => {
        filter += this.filterQuery[key];
      })
      return filter;
    },
    openModal(type, data, rowIndex) {
      switch (type) {
        case('create'): {
          this.$store.commit('modal/openModal', {
            parent: this,
            modal: CreateModal,
            modalName: 'createModal',
            props: {apiKey: this.apiKey, selectOptions: this.selectOptions}
          });
          break;
        }
        case('show'): {
          this.$store.commit('modal/openModal', {parent: this, modal: ShowQuestionModal, modalName: 'showModal', data});
          break;
        }
        case('edit'): {
          this.$store.commit('modal/openModal', {
            parent: this,
            modal: EditQuestionModal,
            modalName: 'createModal',
            data,
            props: {apiKey: this.apiKey, selectOptions: this.selectOptions, currentPage: this.currentPage, rowIndex}
          });
          break;
        }
      }
    },
    createSortQuery() {
      let sorting;
      if (this.orderColumn) {
        if (this.orderDirection === true) {
          sorting = encodeURIComponent('+' + this.orderColumn);
        } else {
          sorting = encodeURIComponent('-' + this.orderColumn);
        }
        if (sorting.includes('categories')) {
          sorting = sorting.replace('categories', 'categoryName');
        }
        return '&sort[]=' + sorting + '&sort[]=id';
      }
      return '';
    }
  },
  computed: {
    slectedIds: function () {
      return this.selectedRows.map(item => item.id);
    },
    locale: function () {
      return this.$store.getters["root/locale"]
          .filter(locale => locale.domainGroup === process.env.VUE_APP_FRONT_GROUP_NAME && locale.status !== 'DRAFT');
    },
    userLocale: function () {
      return this.$store.state.root.user.profile.locale;
    },
    user: function () {
      return this.$store.getters["root/user"];
    },
  },
  watch: {
    showDeleted: function () {
      this.toggleDeletedAmongStatuses();
      this.loadItems()
    }
  }
}
</script>
